/**
 * Página de Aguardando Aprovação
 * 
 * Exibida para usuários sem tenant vinculado, diferenciando entre:
 * - Usuários com convite pendente (aguardam aceitação do admin)
 * - Usuários novos sem convite (aguardam ser convidados)
 * 
 * Layout split-screen premium consistente com Auth.tsx e Onboarding.tsx
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSystemSettings } from '@/hooks/useSystemSettings';
import { Button } from '@/components/ui/button';
import { 
  Clock, 
  LogOut, 
  RefreshCw, 
  Quote, 
  Mail, 
  MessageSquare,
  CheckCircle2,
  Hourglass
} from 'lucide-react';

/**
 * Versículos da Bíblia - Nova Tradução na Linguagem de Hoje (NTLH)
 * Rotacionados aleatoriamente a cada 2 minutos
 */
const versiculosBiblia = [
  { texto: "Confie no SENHOR de todo o coração e não se apoie na sua própria inteligência.", referencia: "Provérbios 3:5" },
  { texto: "Eu sei muito bem os planos que tenho para vocês, planos de paz e não de desgraça, planos para dar a vocês um futuro e uma esperança.", referencia: "Jeremias 29:11" },
  { texto: "O SENHOR é o meu pastor: nada me faltará.", referencia: "Salmos 23:1" },
  { texto: "Tudo posso naquele que me fortalece.", referencia: "Filipenses 4:13" },
  { texto: "Não tenha medo, pois eu estou com você; não fique aflito, pois eu sou o seu Deus. Eu dou forças a você e o ajudo.", referencia: "Isaías 41:10" },
  { texto: "Entreguem todas as suas preocupações a Deus, pois ele cuida de vocês.", referencia: "1 Pedro 5:7" },
  { texto: "O amor é paciente, é bondoso. O amor não é ciumento, nem orgulhoso, nem vaidoso.", referencia: "1 Coríntios 13:4" },
  { texto: "Peçam, e vocês receberão; busquem, e vocês acharão; batam, e a porta será aberta.", referencia: "Mateus 7:7" },
  { texto: "A fé é a certeza de que vamos receber as coisas que esperamos e a prova de que existem coisas que não podemos ver.", referencia: "Hebreus 11:1" },
  { texto: "Venham a mim, todos vocês que estão cansados de carregar as suas pesadas cargas, e eu lhes darei descanso.", referencia: "Mateus 11:28" },
];

const Pending = () => {
  const { 
    user, 
    signOut, 
    loading, 
    dataLoaded,
    hasPendingInvite,
    isTenantMember,
    isAgent
  } = useAuth();
  const { branding } = useSystemSettings();
  const navigate = useNavigate();
  
  // Estado para versículo atual (randômico)
  const [versiculoIndex, setVersiculoIndex] = useState(() => 
    Math.floor(Math.random() * versiculosBiblia.length)
  );

  // Rotaciona versículos a cada 2 minutos
  useEffect(() => {
    const interval = setInterval(() => {
      setVersiculoIndex(prev => {
        let newIndex;
        do {
          newIndex = Math.floor(Math.random() * versiculosBiblia.length);
        } while (newIndex === prev && versiculosBiblia.length > 1);
        return newIndex;
      });
    }, 120000); // 2 minutos

    return () => clearInterval(interval);
  }, []);

  /**
   * Redireciona automaticamente se usuário já é membro de tenant
   * Resolve race condition quando usuário chega nesta página antes
   * do fetchUserData completar, mas depois tem tenant carregado
   */
  useEffect(() => {
    // Aguardar dados carregados antes de verificar
    if (loading || !dataLoaded) return;
    
    // Se não está logado, redirecionar para auth
    if (!user) {
      navigate('/auth', { replace: true });
      return;
    }
    
    // Se já é membro de tenant, redirecionar para área correta
    if (isTenantMember) {
      console.log('[Pending] Usuário já é membro de tenant, redirecionando...');
      if (isAgent) {
        navigate('/tenant/chat', { replace: true });
      } else {
        navigate('/tenant', { replace: true });
      }
    }
  }, [loading, dataLoaded, user, isTenantMember, isAgent, navigate]);

  const versiculoAtual = versiculosBiblia[versiculoIndex];

  /**
   * Recarrega a página para verificar novamente o status
   */
  const handleRefresh = () => {
    window.location.reload();
  };

  /**
   * Executa logout com redirecionamento via window.location
   */
  const handleLogout = () => {
    signOut(() => {
      window.location.href = '/auth';
    });
  };

  // Loading state com spinner centralizado (aguarda dataLoaded também)
  if (loading || !dataLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Se não tem usuário ou já é membro de tenant, não renderiza nada (useEffect cuida do redirect)
  if (!user || isTenantMember) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-background">
      {/* ================================================================== */}
      {/* SIDEBAR ESQUERDA - Branding e Versículo (Oculta em mobile) */}
      {/* ================================================================== */}
      <div className="hidden lg:flex lg:w-[40%] relative bg-gradient-to-br from-[#0a2540] to-[#1a3a5c] p-8 flex-col justify-between overflow-hidden">
        {/* Elementos decorativos de fundo */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -left-20 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-white/3 rounded-full blur-2xl" />
        </div>

        {/* Conteúdo da sidebar */}
        <div className="relative z-10">
          {/* Logo */}
          <div className="mb-12">
            {branding.logo_url ? (
              <img 
                src={branding.logo_url} 
                alt={branding.app_name} 
                className="h-[60px] object-contain brightness-0 invert"
              />
            ) : (
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-white/10">
                  {branding.icon_url ? (
                    <img 
                      src={branding.icon_url} 
                      alt="" 
                      className="h-8 w-8 object-contain brightness-0 invert" 
                    />
                  ) : (
                    <MessageSquare className="h-8 w-8 text-white" />
                  )}
                </div>
                <span className="text-2xl font-bold text-white">{branding.app_name}</span>
              </div>
            )}
          </div>

          {/* Ícone grande decorativo */}
          <div className="flex justify-center my-8">
            <div className="relative">
              <div className="absolute inset-0 bg-white/10 rounded-full blur-xl scale-150" />
              <div className="relative p-8 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
                <Hourglass className="h-16 w-16 text-white animate-pulse" />
              </div>
            </div>
          </div>
        </div>

        {/* Versículo bíblico */}
        <div className="relative z-10">
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
            <Quote className="h-8 w-8 text-white/40 mb-4" />
            <p className="text-white/90 text-lg leading-relaxed mb-4 italic">
              "{versiculoAtual.texto}"
            </p>
            <p className="text-white/60 text-sm font-medium">
              — {versiculoAtual.referencia}
            </p>
          </div>
        </div>
      </div>

      {/* ================================================================== */}
      {/* ÁREA DE CONTEÚDO PRINCIPAL */}
      {/* ================================================================== */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-lg animate-fade-in">
          {/* Header mobile com logo */}
          <div className="lg:hidden flex justify-center mb-8">
            {branding.logo_url ? (
              <img 
                src={branding.logo_url} 
                alt={branding.app_name} 
                className="h-[50px] object-contain"
              />
            ) : (
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-primary/10">
                  {branding.icon_url ? (
                    <img 
                      src={branding.icon_url} 
                      alt="" 
                      className="h-6 w-6 object-contain" 
                    />
                  ) : (
                    <MessageSquare className="h-6 w-6 text-primary" />
                  )}
                </div>
                <span className="text-xl font-bold text-foreground">{branding.app_name}</span>
              </div>
            )}
          </div>

          {/* Ícone principal */}
          <div className="flex justify-center mb-6">
            <div className="p-4 rounded-full bg-warning/10 border border-warning/20">
              <Clock className="h-12 w-12 text-warning" />
            </div>
          </div>

          {/* Título e descrição - diferenciado por estado */}
          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">
              {hasPendingInvite ? 'Convite Pendente' : 'Aguardando Aprovação'}
            </h1>
            <p className="text-muted-foreground text-lg">
              {hasPendingInvite 
                ? 'Você foi convidado para uma equipe'
                : 'Sua conta está ativa, aguardando vínculo com uma empresa'
              }
            </p>
          </div>

          {/* Email do usuário */}
          <div className="flex items-center justify-center gap-2 mb-8 p-3 rounded-xl bg-muted/50 border border-border/50">
            <Mail className="h-5 w-5 text-muted-foreground" />
            <span className="font-medium text-foreground">{user.email}</span>
          </div>

          {/* Próximos passos */}
          <div className="bg-card rounded-2xl p-6 border border-border/50 shadow-sm mb-8">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-primary" />
              Próximos passos
            </h3>
            
            {hasPendingInvite ? (
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-muted-foreground">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center font-medium">1</span>
                  <span>Aguarde o administrador aprovar sua solicitação</span>
                </li>
                <li className="flex items-start gap-3 text-muted-foreground">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center font-medium">2</span>
                  <span>Verifique seu email para novas atualizações</span>
                </li>
                <li className="flex items-start gap-3 text-muted-foreground">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center font-medium">3</span>
                  <span>Entre em contato com o suporte se necessário</span>
                </li>
              </ul>
            ) : (
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-muted-foreground">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center font-medium">1</span>
                  <span>Aguarde receber um convite por email</span>
                </li>
                <li className="flex items-start gap-3 text-muted-foreground">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center font-medium">2</span>
                  <span>Entre em contato com o suporte se necessário</span>
                </li>
              </ul>
            )}
          </div>

          {/* Botões de ação */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              variant="outline" 
              className="flex-1 h-12 rounded-xl border-border/50 hover:bg-muted/50 transition-colors"
              onClick={handleRefresh}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Verificar Novamente
            </Button>
            <Button 
              variant="ghost" 
              className="flex-1 h-12 rounded-xl hover:bg-muted/50 transition-colors"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pending;
